/**
 * Componente para mostrar y seleccionar variables de plantillas de suscripción
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
  Add as AddIcon
} from '@mui/icons-material';

import subscriptionTemplateVariableService from '../../services/subscriptionTemplateVariableService';
import { THEME_COLORS } from '../../constants';

const SubscriptionTemplateVariablesPicker = ({ 
  onVariableSelect, 
  textareaRef, 
  showPreview = false
}) => {
  const [variables, setVariables] = useState(null);
  const [groupedVariables, setGroupedVariables] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(['contact', 'subscription']);

  // Cargar variables al montar el componente
  useEffect(() => {
    loadVariables();
  }, []);

  const loadVariables = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await subscriptionTemplateVariableService.getTemplateVariables();
      
      if (response.success && response.data) {
        setVariables(response.data);
        const grouped = subscriptionTemplateVariableService.groupVariablesByCategory(response.data);
        setGroupedVariables(grouped);
      } else {
        throw new Error('No se pudieron cargar las variables');
      }
    } catch (err) {
      console.error('Error cargando variables:', err);
      setError('Error al cargar las variables de plantillas');
    } finally {
      setLoading(false);
    }
  };

  const handleVariableClick = (variable) => {
    // Si hay un callback personalizado, usarlo
    if (onVariableSelect) {
      onVariableSelect(variable);
      return;
    }

    // Si hay referencia al textarea, insertar directamente
    if (textareaRef && textareaRef.current) {
      subscriptionTemplateVariableService.insertVariableInTextarea(variable, textareaRef.current);
    }
  };

  const handleCopyVariable = (variable, event) => {
    event.stopPropagation(); // Evitar que se dispare el click del chip
    
    navigator.clipboard.writeText(variable).then(() => {
      // Aquí podrías mostrar un toast de éxito
      console.log(`Variable ${variable} copiada al portapapeles`);
    }).catch(err => {
      console.error('Error copiando al portapapeles:', err);
    });
  };

  const handleCategoryToggle = (category) => {
    setExpandedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(cat => cat !== category)
        : [...prev, category]
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ ml: 2, color: THEME_COLORS.TEXT_SECONDARY }}>
          Cargando variables...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!variables || Object.keys(groupedVariables).length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No hay variables disponibles
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ 
          color: THEME_COLORS.TEXT_PRIMARY, 
          fontSize: '1rem',
          fontWeight: 600,
          mb: 1 
        }}>
          Variables Disponibles para Suscripciones
        </Typography>
        <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
          Haz clic en cualquier variable para insertarla en tu plantilla de suscripción
        </Typography>
      </Box>

      {/* Variables por categoría */}
      <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
        {Object.entries(groupedVariables).map(([category, categoryData]) => (
          <Accordion
            key={category}
            expanded={expandedCategories.includes(category)}
            onChange={() => handleCategoryToggle(category)}
            sx={{
              mb: 1,
              border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
              borderRadius: '8px',
              '&:before': { display: 'none' },
              '& .MuiAccordionSummary-root': {
                backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
                borderRadius: '8px',
                minHeight: '48px'
              },
              '& .MuiAccordionDetails-root': {
                backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
                borderTop: `1px solid ${THEME_COLORS.BORDER_LIGHT}`
              }
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 600, 
                color: THEME_COLORS.TEXT_PRIMARY 
              }}>
                {categoryData.displayName}
              </Typography>
              <Typography variant="caption" sx={{ 
                ml: 2, 
                color: THEME_COLORS.TEXT_SECONDARY 
              }}>
                ({categoryData.variables.length} variables)
              </Typography>
            </AccordionSummary>
            
            <AccordionDetails>
              <Grid container spacing={1}>
                {categoryData.variables.map((variable, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
                          borderColor: THEME_COLORS.TEXT_PRIMARY,
                          transform: 'translateY(-1px)'
                        }
                      }}
                      onClick={() => handleVariableClick(variable.variable)}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        mb: 1 
                      }}>
                        <Chip
                          label={variable.displayName}
                          size="small"
                          icon={<AddIcon sx={{ fontSize: '16px' }} />}
                          sx={{
                            backgroundColor: THEME_COLORS.TEXT_PRIMARY,
                            color: THEME_COLORS.WHITE,
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            '& .MuiChip-icon': {
                              color: THEME_COLORS.WHITE
                            }
                          }}
                        />
                        <Tooltip title="Copiar variable">
                          <IconButton
                            size="small"
                            onClick={(e) => handleCopyVariable(variable.variable, e)}
                            sx={{ 
                              color: THEME_COLORS.TEXT_SECONDARY,
                              '&:hover': { 
                                color: THEME_COLORS.TEXT_PRIMARY,
                                backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT 
                              }
                            }}
                          >
                            <CopyIcon sx={{ fontSize: '16px' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      
                      <Typography variant="caption" sx={{ 
                        color: THEME_COLORS.TEXT_SECONDARY,
                        fontSize: '0.75rem',
                        lineHeight: 1.2
                      }}>
                        {variable.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* Footer con información */}
      <Box sx={{ 
        mt: 2, 
        p: 2, 
        backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
        borderRadius: '8px',
        border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`
      }}>
        <Typography variant="caption" sx={{ 
          color: THEME_COLORS.TEXT_SECONDARY,
          fontStyle: 'italic' 
        }}>
          💡 Las variables se reemplazan automáticamente con datos reales de contactos y suscripciones cuando se envían los mensajes.
        </Typography>
      </Box>
    </Box>
  );
};

export default SubscriptionTemplateVariablesPicker; 